import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { nistCsfSspAPI, assessmentAPI } from '../lib/api';
import {
  ArrowLeft, Download, Shield, CheckCircle2, XCircle, AlertTriangle,
  FileCheck, ChevronDown, ChevronUp, BarChart3, Eye, EyeOff,
  FileText, TrendingUp, Database, Layers,
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType, AlignmentType, ShadingType } from 'docx';
import { saveAs } from 'file-saver';

/* ─────── Shared Components ─────── */
function ProgressBar({ percent, color = 'violet', height = 'h-2' }) {
  const colors = { violet: 'bg-violet-500', emerald: 'bg-emerald-500', amber: 'bg-amber-500', red: 'bg-red-500', cyan: 'bg-cyan-500', blue: 'bg-blue-500' };
  return (
    <div className={`${height} bg-slate-800 rounded-full overflow-hidden`}>
      <div className={`h-full ${colors[color] || colors.violet} rounded-full transition-all duration-700`} style={{ width: `${Math.min(percent, 100)}%` }} />
    </div>
  );
}

function ScoreRing({ percent, size = 160, stroke = 12, label }) {
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (percent / 100) * circ;
  const color = percent >= 80 ? '#10b981' : percent >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="rgba(100,116,139,0.12)" strokeWidth={stroke} />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-700" />
      </svg>
      <div className="absolute flex flex-col items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-4xl font-bold text-white">{percent}%</span>
        {label && <span className="text-xs text-slate-500 mt-1">{label}</span>}
      </div>
    </div>
  );
}

/* ─────── Tab: Dashboard ─────── */
function DashboardTab({ summary, score }) {
  if (!summary || !score) return null;
  const funcs = Object.entries(summary.functions || {});
  const funcColors = { Govern: 'violet', Identify: 'blue', Protect: 'emerald', Detect: 'amber', Respond: 'red', Recover: 'cyan' };
  return (
    <div className="space-y-8">
      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Implementation Rate', value: `${summary.implementation_rate}%`, sub: `${summary.controls.implemented}/${summary.controls.total} controls`, icon: Shield, color: 'violet' },
          { label: 'MUST Compliance', value: `${summary.mustStatements.total > 0 ? Math.round((summary.mustStatements.met/summary.mustStatements.total)*100) : 0}%`, sub: `${summary.mustStatements.met}/${summary.mustStatements.total} met`, icon: CheckCircle2, color: 'emerald' },
          { label: 'Evidence Collection', value: `${summary.evidence.total > 0 ? Math.round((summary.evidence.collected/summary.evidence.total)*100) : 0}%`, sub: `${summary.evidence.collected}/${summary.evidence.total} items`, icon: FileCheck, color: 'cyan' },
          { label: 'Controls Outstanding', value: summary.controls.not_implemented, sub: 'requiring remediation', icon: AlertTriangle, color: 'amber' },
        ].map(card => (
          <div key={card.label} className={`bg-${card.color}-500/10 border border-${card.color}-500/20 rounded-xl p-5`}>
            <div className="flex items-center gap-2 mb-2">
              <card.icon className={`w-4 h-4 text-${card.color}-400`} />
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{card.label}</span>
            </div>
            <div className="text-3xl font-bold text-white">{card.value}</div>
            <div className="text-sm text-slate-500 mt-1">{card.sub}</div>
          </div>
        ))}
      </div>

      {/* Function Breakdown */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">CSF Function Breakdown</h3>
        <div className="grid grid-cols-3 gap-4">
          {funcs.map(([name, info]) => {
            const pct = info.total > 0 ? Math.round((info.compliant / info.total) * 100) : 0;
            return (
              <div key={name} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-white">{name}</span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                    pct >= 80 ? 'bg-emerald-500/20 text-emerald-400' : pct >= 50 ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'
                  }`}>{pct}%</span>
                </div>
                <ProgressBar percent={pct} color={funcColors[name] || 'violet'} />
                <div className="flex justify-between mt-2 text-xs text-slate-500">
                  <span>{info.compliant} compliant</span>
                  <span>{info.total} total</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Response Distribution */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Response Distribution</h3>
        <div className="flex items-center gap-6">
          <div className="flex-1">
            {score?.familyScores?.map(f => (
              <div key={f.name} className="flex items-center gap-3 py-2 border-b border-slate-800 last:border-0">
                <span className="w-24 text-sm font-mono text-slate-400">{f.abbrev}</span>
                <span className="w-32 text-sm text-slate-300">{f.name}</span>
                <div className="flex-1 flex items-center gap-1 h-6">
                  {f.compliant > 0 && <div className="bg-emerald-500 h-full rounded-l" style={{ width: `${(f.compliant / f.totalControls) * 100}%` }} />}
                  {f.nonCompliant > 0 && <div className="bg-red-500 h-full" style={{ width: `${(f.nonCompliant / f.totalControls) * 100}%` }} />}
                  {f.notApplicable > 0 && <div className="bg-slate-600 h-full rounded-r" style={{ width: `${(f.notApplicable / f.totalControls) * 100}%` }} />}
                </div>
                <span className="text-sm text-slate-400 w-16 text-right">{f.percentComplete}%</span>
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-2 text-xs">
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-emerald-500" />Compliant</div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-red-500" />Non-Compliant</div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-slate-600" />N/A</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────── Tab: Compliance Score ─────── */
function ComplianceScoreTab({ score }) {
  if (!score) return null;
  return (
    <div className="space-y-8">
      {/* Overall Score */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-8 flex items-center gap-12">
        <div className="relative">
          <ScoreRing percent={score.overallScore.percentage} label="Compliant" />
        </div>
        <div className="flex-1 space-y-4">
          <h3 className="text-2xl font-bold text-white">Overall Compliance Score</h3>
          <p className="text-slate-400">{score.overallScore.displayText} applicable controls implemented</p>
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div className="bg-emerald-500/10 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-emerald-400">{score.summary.compliant}</div>
              <div className="text-xs text-slate-500">Compliant</div>
            </div>
            <div className="bg-red-500/10 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-red-400">{score.summary.nonCompliant}</div>
              <div className="text-xs text-slate-500">Non-Compliant</div>
            </div>
            <div className="bg-slate-500/10 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-slate-400">{score.summary.notApplicable}</div>
              <div className="text-xs text-slate-500">Not Applicable</div>
            </div>
          </div>
        </div>
      </div>

      {/* Per-Function Score */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Score by CSF Function</h3>
        <div className="space-y-4">
          {score.familyScores.map(f => (
            <div key={f.name} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-mono font-bold px-2 py-0.5 rounded" style={{ backgroundColor: `${f.color}20`, color: f.color }}>{f.abbrev}</span>
                  <span className="font-semibold text-white">{f.name}</span>
                </div>
                <span className={`text-sm font-bold ${f.percentComplete >= 80 ? 'text-emerald-400' : f.percentComplete >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                  {f.percentComplete}%
                </span>
              </div>
              <ProgressBar percent={f.percentComplete} color={f.percentComplete >= 80 ? 'emerald' : f.percentComplete >= 50 ? 'amber' : 'red'} />
              <div className="flex justify-between mt-2 text-xs text-slate-500">
                <span>{f.compliant} implemented</span>
                <span>{f.nonCompliant} gaps</span>
                <span>{f.totalControls} total</span>
              </div>
              {/* Category breakdown */}
              {f.categories && f.categories.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-700 grid grid-cols-2 gap-2">
                  {f.categories.map(cat => {
                    const catPct = (cat.total - cat.notApplicable) > 0 ? Math.round((cat.compliant / (cat.total - cat.notApplicable)) * 100) : 0;
                    return (
                      <div key={cat.name} className="flex items-center justify-between text-xs">
                        <span className="text-slate-400 truncate">{cat.name}</span>
                        <span className={catPct >= 80 ? 'text-emerald-400' : catPct >= 50 ? 'text-amber-400' : 'text-red-400'}>
                          {cat.compliant}/{cat.total} ({catPct}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────── Tab: Detailed Controls ─────── */
function DetailedControlsTab({ assessmentId }) {
  const [data, setData] = useState(null);
  const [gapsOnly, setGapsOnly] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    nistCsfSspAPI.getDetailedControls(assessmentId, gapsOnly).then(r => setData(r.data));
  }, [assessmentId, gapsOnly]);

  const toggleExpand = (funcName) => setExpanded(prev => ({ ...prev, [funcName]: !prev[funcName] }));

  if (!data) return <div className="text-center text-slate-500 py-12">Loading controls...</div>;

  return (
    <div className="space-y-6">
      {/* Toggle */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">
          {gapsOnly ? 'Non-Compliant Controls' : 'All Security Controls'} ({data.totalQuestions})
        </h3>
        <button
          data-testid="toggle-gaps-only"
          onClick={() => setGapsOnly(!gapsOnly)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            gapsOnly ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-slate-700 text-slate-300 border border-slate-600'
          }`}
        >
          {gapsOnly ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          {gapsOnly ? 'Showing Gaps Only' : 'Show Gaps Only'}
        </button>
      </div>

      {/* Functions */}
      {data.categories.map(func => (
        <div key={func.name} className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleExpand(func.name)}
            className="w-full flex items-center justify-between p-5 hover:bg-slate-800/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-sm font-mono font-bold px-2 py-0.5 rounded bg-violet-500/20 text-violet-400">{func.abbrev}</span>
              <span className="text-lg font-semibold text-white">{func.name}</span>
              <span className="text-sm text-slate-500">({func.controls.length} controls)</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-xs text-emerald-400">{func.stats.yes} Yes</span>
              <span className="text-xs text-red-400">{func.stats.no} No</span>
              {expanded[func.name] ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
            </div>
          </button>

          {expanded[func.name] && (
            <div className="border-t border-slate-800">
              {func.controls.map(ctrl => (
                <div key={ctrl.id} className="border-b border-slate-800/50 p-5 hover:bg-slate-800/20">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-mono text-slate-500">{ctrl.id}</span>
                      {ctrl.priorityNumber && (
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          ctrl.priorityNumber <= 20 ? 'bg-red-500/20 text-red-400' :
                          ctrl.priorityNumber <= 50 ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-500/20 text-slate-400'
                        }`}>P{ctrl.priorityNumber}</span>
                      )}
                      <span className="text-xs text-slate-600">{ctrl.category}</span>
                    </div>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                      ctrl.response.answer === 'Y' ? 'bg-emerald-500/20 text-emerald-400' :
                      ctrl.response.answer === 'NA' ? 'bg-slate-500/20 text-slate-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {ctrl.response.answer === 'Y' ? 'Implemented' : ctrl.response.answer === 'NA' ? 'N/A' : 'Not Implemented'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-300 mb-3">{ctrl.text}</p>

                  {/* MUST Statement */}
                  {ctrl.mustStatement && (
                    <div className={`text-xs px-3 py-2 rounded mb-2 ${
                      ctrl.mustStatus === 'DOES' ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' :
                      ctrl.mustStatus === 'DOES_NOT' ? 'bg-red-500/10 border border-red-500/20 text-red-400' :
                      'bg-slate-800 border border-slate-700 text-slate-400'
                    }`}>
                      <span className="font-bold">{ctrl.mustStatus === 'DOES' ? 'DOES' : ctrl.mustStatus === 'DOES_NOT' ? 'DOES NOT' : 'UNSET'}:</span> {ctrl.mustStatement.substring(0, 200)}{ctrl.mustStatement.length > 200 ? '...' : ''}
                    </div>
                  )}

                  {/* Evidence */}
                  {ctrl.evidenceRequired.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {ctrl.evidenceRequired.map((ev, idx) => {
                        const collected = ctrl.evidenceChecklist[String(idx)];
                        return (
                          <span key={idx} className={`text-xs px-2 py-1 rounded ${collected ? 'bg-cyan-500/15 text-cyan-400' : 'bg-slate-800 text-slate-500'}`}>
                            {collected ? '✓ ' : '○ '}{ev.substring(0, 40)}{ev.length > 40 ? '...' : ''}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {/* Response text */}
                  {ctrl.response.responseText && (
                    <div className="mt-2 text-xs text-slate-500 italic">"{ctrl.response.responseText}"</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ─────── Tab: Evidence Checklist ─────── */
function EvidenceTab({ assessmentId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    nistCsfSspAPI.getEvidenceChecklist(assessmentId).then(r => setData(r.data));
  }, [assessmentId]);

  if (!data) return <div className="text-center text-slate-500 py-12">Loading evidence...</div>;

  const pct = data.totalItems > 0 ? Math.round((data.collectedItems / data.totalItems) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 flex items-center gap-8">
        <div className="relative">
          <ScoreRing percent={pct} size={120} stroke={10} label="Collected" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-white">{data.collectedItems}/{data.totalItems} Evidence Items Collected</h3>
          <p className="text-sm text-slate-500 mt-1">Unique evidence requirements across all 106 controls</p>
        </div>
      </div>

      {Object.entries(data.categories).map(([cat, items]) => {
        if (!items || items.length === 0) return null;
        const catCollected = items.filter(i => i.collected).length;
        return (
          <div key={cat} className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-white">{cat}</h4>
              <span className="text-xs text-slate-500">{catCollected}/{items.length} collected</span>
            </div>
            <div className="space-y-2">
              {items.map((item, idx) => (
                <div key={idx} className={`flex items-start gap-3 p-2.5 rounded-lg text-sm ${
                  item.collected ? 'bg-emerald-500/10 border border-emerald-500/15' : 'bg-slate-800/40 border border-transparent'
                }`}>
                  <span className={`mt-0.5 ${item.collected ? 'text-emerald-400' : 'text-slate-600'}`}>
                    {item.collected ? '✓' : '○'}
                  </span>
                  <div className="flex-1">
                    <span className={item.collected ? 'text-slate-400' : 'text-slate-300'}>{item.item}</span>
                    <div className="text-xs text-slate-600 mt-1">{item.sources.length} control{item.sources.length !== 1 ? 's' : ''}: {item.sources.slice(0, 5).join(', ')}{item.sources.length > 5 ? '...' : ''}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─────── Tab: Prioritization ─────── */
function PrioritizationTab({ assessmentId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    nistCsfSspAPI.getPrioritization(assessmentId).then(r => setData(r.data));
  }, [assessmentId]);

  if (!data) return <div className="text-center text-slate-500 py-12">Loading prioritization...</div>;

  return (
    <div className="space-y-6">
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-2">CSET Security Control Prioritization</h3>
        <p className="text-sm text-slate-500">Controls ranked by CSET priority number (1 = highest priority). Based on NIST SP 800-18 Rev 1 guidance.</p>
        <div className="flex items-center gap-6 mt-4 text-sm">
          <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-emerald-500" />{data.summary.compliant} Compliant</span>
          <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-red-500" />{data.summary.nonCompliant} Non-Compliant</span>
        </div>
      </div>

      <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-3">Priority</th>
              <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-3">Control</th>
              <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-3">Function</th>
              <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-3">Description</th>
              <th className="text-center text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-3">Status</th>
              <th className="text-center text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-3">MUST</th>
              <th className="text-center text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-3">Evidence</th>
            </tr>
          </thead>
          <tbody>
            {data.data.map(row => (
              <tr key={row.secCntrl} className="border-b border-slate-800/50 hover:bg-slate-800/20 transition-colors">
                <td className="px-5 py-3">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                    row.sort <= 20 ? 'bg-red-500/20 text-red-400' : row.sort <= 50 ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-500/20 text-slate-400'
                  }`}>P{row.sort}</span>
                </td>
                <td className="px-5 py-3 text-sm font-mono text-slate-300">{row.secCntrl}</td>
                <td className="px-5 py-3 text-sm text-slate-400">{row.function}</td>
                <td className="px-5 py-3 text-sm text-slate-300 max-w-sm truncate">{row.description}</td>
                <td className="px-5 py-3 text-center">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                    row.status === 'Y' ? 'bg-emerald-500/20 text-emerald-400' : row.status === 'NA' ? 'bg-slate-500/20 text-slate-400' : 'bg-red-500/20 text-red-400'
                  }`}>{row.status === 'Y' ? 'YES' : row.status === 'NA' ? 'N/A' : 'NO'}</span>
                </td>
                <td className="px-5 py-3 text-center text-xs">
                  <span className={row.mustStatus === 'DOES' ? 'text-emerald-400' : row.mustStatus === 'DOES_NOT' ? 'text-red-400' : 'text-slate-600'}>
                    {row.mustStatus || '—'}
                  </span>
                </td>
                <td className="px-5 py-3 text-center text-xs text-slate-400">{row.evidenceCollected}/{row.evidenceTotal}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────── System Overview Tab ─────── */
function SystemOverviewTab({ summary }) {
  return (
    <div className="space-y-6">
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">System Security Plan Overview</h3>
        <p className="text-sm text-slate-400 leading-relaxed mb-4">
          This System Security Plan (SSP) is developed in accordance with <strong className="text-white">NIST SP 800-18 Rev 1</strong> — Guide for Developing Security Plans for Federal Information Systems — and aligned to the <strong className="text-white">NIST Cybersecurity Framework (CSF) 2.0</strong>.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <span className="text-xs text-slate-500 uppercase tracking-wider">Framework</span>
            <p className="text-white font-semibold mt-1">NIST Cybersecurity Framework 2.0</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <span className="text-xs text-slate-500 uppercase tracking-wider">SSP Guide</span>
            <p className="text-white font-semibold mt-1">NIST SP 800-18 Revision 1</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <span className="text-xs text-slate-500 uppercase tracking-wider">Total Controls</span>
            <p className="text-white font-semibold mt-1">{summary?.controls?.total || 106} Security Controls</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <span className="text-xs text-slate-500 uppercase tracking-wider">Functions</span>
            <p className="text-white font-semibold mt-1">6 CSF Functions (GV, ID, PR, DE, RS, RC)</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">CSF 2.0 Functions</h3>
        <div className="space-y-3">
          {[
            { abbrev: 'GV', name: 'Govern', desc: 'Establish and monitor the organization\'s cybersecurity risk management strategy, expectations, and policy.', color: '#8b5cf6' },
            { abbrev: 'ID', name: 'Identify', desc: 'Help determine the current cybersecurity risk to the organization.', color: '#3b82f6' },
            { abbrev: 'PR', name: 'Protect', desc: 'Use safeguards to prevent or reduce cybersecurity risk.', color: '#10b981' },
            { abbrev: 'DE', name: 'Detect', desc: 'Find and analyze possible cybersecurity attacks and compromises.', color: '#f59e0b' },
            { abbrev: 'RS', name: 'Respond', desc: 'Take action regarding a detected cybersecurity incident.', color: '#ef4444' },
            { abbrev: 'RC', name: 'Recover', desc: 'Restore assets and operations that were impacted by a cybersecurity incident.', color: '#06b6d4' },
          ].map(func => (
            <div key={func.abbrev} className="flex items-start gap-4 p-3 bg-slate-800/30 rounded-lg border border-slate-700">
              <span className="text-sm font-mono font-bold px-2.5 py-1 rounded" style={{ backgroundColor: `${func.color}20`, color: func.color }}>{func.abbrev}</span>
              <div>
                <span className="font-semibold text-white">{func.name}</span>
                <p className="text-sm text-slate-400 mt-0.5">{func.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────── Main Component ─────── */
const TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { key: 'score', label: 'Compliance Score', icon: TrendingUp },
  { key: 'overview', label: 'System Overview', icon: Database },
  { key: 'controls', label: 'Detailed Controls', icon: Layers },
  { key: 'evidence', label: 'Evidence Checklist', icon: FileCheck },
  { key: 'prioritization', label: 'Prioritization', icon: AlertTriangle },
];

export default function NistCsfSspReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [summary, setSummary] = useState(null);
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatingWord, setGeneratingWord] = useState(false);
  const [detailedData, setDetailedData] = useState(null);
  const [priorityData, setPriorityData] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [sumRes, scoreRes, detRes, priRes] = await Promise.all([
          nistCsfSspAPI.getSummary(id),
          nistCsfSspAPI.getComplianceScore(id),
          nistCsfSspAPI.getDetailedControls(id, false),
          nistCsfSspAPI.getPrioritization(id),
        ]);
        setSummary(sumRes.data);
        setScore(scoreRes.data);
        setDetailedData(detRes.data);
        setPriorityData(priRes.data);
      } catch (err) {
        console.error('Failed to load SSP data:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  /* ─── PDF Header helper ─── */
  const addPdfHeader = (doc, pw, title) => {
    doc.setFillColor(139, 92, 246);
    doc.rect(0, 0, pw, 3, 'F');
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(title, 20, 25);
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(20, 30, pw - 20, 30);
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text('NIST CSF 2.0 System Security Plan', pw - 20, 25, { align: 'right' });
  };

  /* ─── Generate PDF ─── */
  const generatePDF = async () => {
    if (!summary || !score || !detailedData || !priorityData) return;
    setGenerating(true);
    try {
      const doc = new jsPDF();
      const pw = doc.internal.pageSize.getWidth();
      const ph = doc.internal.pageSize.getHeight();

      // ── COVER PAGE ──
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, pw, ph, 'F');
      doc.setFillColor(139, 92, 246); doc.rect(0, 0, pw, 6, 'F');
      doc.setFillColor(59, 130, 246); doc.rect(0, 6, pw, 2, 'F');
      doc.setTextColor(255,255,255); doc.setFontSize(14); doc.setFont(undefined,'bold');
      doc.text('Colt Cybersecurity Consultants', 20, 30);
      doc.setFontSize(16); doc.text('Aspiritech', pw - 50, 30);
      doc.setFontSize(8); doc.setTextColor(251,191,36); doc.text('Cybersecurity', pw - 50, 38);
      doc.setTextColor(255,255,255); doc.setFontSize(36); doc.setFont(undefined,'bold');
      doc.text('System Security Plan', pw/2, 80, {align:'center'});
      doc.setFontSize(20); doc.setTextColor(139,92,246);
      doc.text('NIST CSF 2.0', pw/2, 100, {align:'center'});
      doc.setFontSize(14); doc.setTextColor(148,163,184);
      doc.text(summary.assessmentName || 'NIST CSF 2.0 Assessment', pw/2, 115, {align:'center'});
      // Stats box
      doc.setFillColor(30,41,59); doc.roundedRect(30, 135, pw-60, 60, 5, 5, 'F');
      doc.setDrawColor(139,92,246); doc.setLineWidth(0.5); doc.roundedRect(30, 135, pw-60, 60, 5, 5, 'S');
      doc.setFontSize(11); doc.setTextColor(255,255,255);
      doc.text('Framework:', 45, 152); doc.setTextColor(139,92,246); doc.text('NIST Cybersecurity Framework 2.0', 90, 152);
      doc.setTextColor(255,255,255); doc.text('SSP Guide:', 45, 165); doc.setTextColor(59,130,246); doc.text('NIST SP 800-18 Revision 1', 85, 165);
      doc.setTextColor(255,255,255); doc.text('Implementation Rate:', 45, 178); doc.setTextColor(16,185,129); doc.text(`${summary.implementation_rate}%`, 105, 178);
      doc.setTextColor(255,255,255); doc.text('Total Controls:', 130, 178); doc.setTextColor(59,130,246); doc.text(`${summary.controls.total}`, 168, 178);
      doc.setFontSize(14); doc.setTextColor(251,191,36); doc.text('Masks Down. Shields Up.', pw/2, 230, {align:'center'});
      doc.setFontSize(9); doc.setTextColor(100,116,139);
      doc.text('Prepared by Colt Cybersecurity Consultants in partnership with Aspiritech', pw/2, ph-20, {align:'center'});

      // ── TABLE OF CONTENTS ──
      doc.addPage(); addPdfHeader(doc, pw, 'Table of Contents');
      let y = 50;
      const toc = [
        {n:'1', t:'Compliance Score Overview'}, {n:'2', t:'CSF Function Scores'},
        {n:'3', t:'Detailed Controls by Function'}, {n:'4', t:'MUST Statement Status'},
        {n:'5', t:'Evidence Collection Status'}, {n:'6', t:'CSET Prioritization'},
      ];
      toc.forEach(item => {
        doc.setFont(undefined,'bold'); doc.setTextColor(139,92,246); doc.text(item.n+'.', 25, y);
        doc.setFont(undefined,'normal'); doc.setTextColor(30,41,59); doc.text(item.t, 35, y);
        y += 12;
      });

      // ── 1. COMPLIANCE SCORE ──
      doc.addPage(); addPdfHeader(doc, pw, '1. Compliance Score Overview');
      y = 50;
      doc.setFontSize(11); doc.setTextColor(30,41,59);
      doc.setFillColor(30,41,59); doc.roundedRect(20, y-5, pw-40, 40, 3, 3, 'F');
      doc.setFontSize(28); doc.setFont(undefined,'bold'); doc.setTextColor(16,185,129);
      doc.text(`${score.overallScore.percentage}%`, 50, y+15);
      doc.setFontSize(10); doc.setTextColor(148,163,184); doc.text('Overall Compliance', 45, y+25);
      doc.setFontSize(16); doc.setTextColor(16,185,129); doc.text(String(score.summary.compliant), pw/2, y+12);
      doc.setFontSize(8); doc.setTextColor(148,163,184); doc.text('Compliant', pw/2 - 5, y+20);
      doc.setFontSize(16); doc.setTextColor(239,68,68); doc.text(String(score.summary.nonCompliant), pw/2+50, y+12);
      doc.setFontSize(8); doc.setTextColor(148,163,184); doc.text('Non-Compliant', pw/2+40, y+20);
      doc.setFontSize(16); doc.setTextColor(100,116,139); doc.text(String(score.summary.notApplicable), pw/2+100, y+12);
      doc.setFontSize(8); doc.setTextColor(148,163,184); doc.text('N/A', pw/2+103, y+20);
      y += 55;
      // MUST & Evidence stats
      doc.setFontSize(10); doc.setFont(undefined,'bold'); doc.setTextColor(139,92,246);
      doc.text('MUST Statements:', 20, y);
      doc.setFont(undefined,'normal'); doc.setTextColor(50,50,50);
      doc.text(`${summary.mustStatements.met} of ${summary.mustStatements.total} met (${summary.mustStatements.total > 0 ? Math.round((summary.mustStatements.met/summary.mustStatements.total)*100) : 0}%)`, 70, y);
      y += 10;
      doc.setFont(undefined,'bold'); doc.setTextColor(59,130,246);
      doc.text('Evidence:', 20, y);
      doc.setFont(undefined,'normal'); doc.setTextColor(50,50,50);
      doc.text(`${summary.evidence.collected} of ${summary.evidence.total} collected (${summary.evidence.total > 0 ? Math.round((summary.evidence.collected/summary.evidence.total)*100) : 0}%)`, 55, y);

      // ── 2. FUNCTION SCORES ──
      doc.addPage(); addPdfHeader(doc, pw, '2. CSF Function Scores');
      y = 50;
      autoTable(doc, {
        startY: y,
        head: [['Function', 'Abbrev', 'Compliant', 'Non-Compliant', 'N/A', 'Total', 'Score']],
        body: score.familyScores.map(f => [f.name, f.abbrev, f.compliant, f.nonCompliant, f.notApplicable, f.totalControls, `${f.percentComplete}%`]),
        headStyles: { fillColor: [139,92,246], textColor: [255,255,255], fontSize: 9 },
        bodyStyles: { textColor: [30,41,59], fontSize: 9 },
        alternateRowStyles: { fillColor: [248,250,252] },
        margin: { left: 20, right: 20 },
      });
      y = doc.lastAutoTable.finalY + 15;
      // Category details per function
      score.familyScores.forEach(f => {
        if (y > ph - 60) { doc.addPage(); addPdfHeader(doc, pw, '2. CSF Function Scores (continued)'); y = 50; }
        doc.setFont(undefined,'bold'); doc.setTextColor(139,92,246); doc.setFontSize(11);
        doc.text(`${f.abbrev} - ${f.name}`, 20, y); y += 5;
        if (f.categories && f.categories.length > 0) {
          autoTable(doc, {
            startY: y,
            head: [['Category', 'Compliant', 'Non-Compliant', 'N/A', 'Total']],
            body: f.categories.map(c => [c.name, c.compliant, c.nonCompliant, c.notApplicable, c.total]),
            headStyles: { fillColor: [59,130,246], textColor: [255,255,255], fontSize: 8 },
            bodyStyles: { fontSize: 8 }, margin: { left: 25, right: 25 },
          });
          y = doc.lastAutoTable.finalY + 10;
        }
      });

      // ── 3. DETAILED CONTROLS ──
      doc.addPage(); addPdfHeader(doc, pw, '3. Detailed Controls by Function');
      y = 50;
      detailedData.categories.forEach(func => {
        if (y > ph - 40) { doc.addPage(); addPdfHeader(doc, pw, `3. Controls - ${func.name}`); y = 50; }
        doc.setFont(undefined,'bold'); doc.setTextColor(139,92,246); doc.setFontSize(12);
        doc.text(`${func.abbrev} - ${func.name}`, 20, y);
        doc.setFont(undefined,'normal'); doc.setFontSize(8); doc.setTextColor(100,116,139);
        doc.text(`${func.stats.yes} Yes | ${func.stats.no} No | ${func.stats.na} N/A`, pw-60, y); y += 5;
        autoTable(doc, {
          startY: y,
          head: [['ID', 'P#', 'Control', 'Status', 'MUST']],
          body: func.controls.map(c => [
            c.id, c.priorityNumber ? `P${c.priorityNumber}` : '-',
            c.text.substring(0, 80) + (c.text.length > 80 ? '...' : ''),
            c.response.answer === 'Y' ? 'Yes' : c.response.answer === 'NA' ? 'N/A' : 'No',
            c.mustStatus === 'DOES' ? 'DOES' : c.mustStatus === 'DOES_NOT' ? 'DOES NOT' : '-',
          ]),
          headStyles: { fillColor: [30,41,59], textColor: [255,255,255], fontSize: 7 },
          bodyStyles: { fontSize: 7 },
          columnStyles: { 0: {cellWidth: 22}, 1: {cellWidth: 12}, 2: {cellWidth: 'auto'}, 3: {cellWidth: 15}, 4: {cellWidth: 22} },
          margin: { left: 20, right: 20 },
          didParseCell: (data) => {
            if (data.section === 'body' && data.column.index === 3) {
              if (data.cell.raw === 'Yes') data.cell.styles.textColor = [16,185,129];
              else if (data.cell.raw === 'No') data.cell.styles.textColor = [239,68,68];
            }
          },
        });
        y = doc.lastAutoTable.finalY + 12;
      });

      // ── 4. PRIORITIZATION ──
      doc.addPage(); addPdfHeader(doc, pw, '6. CSET Security Control Prioritization');
      autoTable(doc, {
        startY: 50,
        head: [['Priority', 'Control', 'Function', 'Status', 'MUST', 'Evidence']],
        body: priorityData.data.map(r => [
          `P${r.sort}`, r.secCntrl, r.function,
          r.status === 'Y' ? 'Yes' : r.status === 'NA' ? 'N/A' : 'No',
          r.mustStatus || '-', `${r.evidenceCollected}/${r.evidenceTotal}`,
        ]),
        headStyles: { fillColor: [139,92,246], textColor: [255,255,255], fontSize: 8 },
        bodyStyles: { fontSize: 7 },
        margin: { left: 20, right: 20 },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index === 3) {
            if (data.cell.raw === 'Yes') data.cell.styles.textColor = [16,185,129];
            else if (data.cell.raw === 'No') data.cell.styles.textColor = [239,68,68];
          }
        },
      });

      // ── Footer on all pages ──
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7); doc.setTextColor(148,163,184);
        doc.text(`Page ${i} of ${pageCount}`, pw/2, ph-10, {align:'center'});
        doc.text('CONFIDENTIAL', pw-25, ph-10, {align:'right'});
      }

      doc.save(`NIST_CSF_SSP_Report_${new Date().toISOString().slice(0,10)}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
    } finally {
      setGenerating(false);
    }
  };

  /* ─── Generate Word Document ─── */
  const generateWordDoc = async () => {
    if (!summary || !score || !detailedData || !priorityData) return;
    setGeneratingWord(true);
    try {
      const children = [];

      // Title
      children.push(
        new Paragraph({ children: [new TextRun({ text: 'System Security Plan', bold: true, size: 56, color: '4F46E5' })], alignment: AlignmentType.CENTER, spacing: { after: 200 } }),
        new Paragraph({ children: [new TextRun({ text: 'NIST Cybersecurity Framework 2.0', bold: true, size: 36, color: '6B7280' })], alignment: AlignmentType.CENTER, spacing: { after: 200 } }),
        new Paragraph({ children: [new TextRun({ text: summary.assessmentName || 'NIST CSF 2.0 Assessment', bold: true, size: 32, color: '7C3AED' })], alignment: AlignmentType.CENTER, spacing: { after: 200 } }),
        new Paragraph({ children: [new TextRun({ text: 'NIST SP 800-18 Rev 1', size: 24, color: '6B7280' })], alignment: AlignmentType.CENTER, spacing: { after: 600 } }),
        new Paragraph({ children: [new TextRun({ text: 'Prepared by Colt Cybersecurity Consultants in partnership with Aspiritech', size: 20, color: '374151' })], alignment: AlignmentType.CENTER, spacing: { after: 200 } }),
        new Paragraph({ children: [new TextRun({ text: `Generated: ${new Date().toLocaleDateString()}`, size: 20, color: '6B7280' })], alignment: AlignmentType.CENTER, spacing: { after: 400 } }),
        new Paragraph({ children: [new TextRun({ text: 'This report includes:', bold: true, size: 22 })], spacing: { after: 100 } }),
        new Paragraph({ children: [new TextRun({ text: `• ${summary.controls.total} NIST CSF 2.0 Security Controls with Responses`, size: 20 })], indent: { left: 400 } }),
        new Paragraph({ children: [new TextRun({ text: '• MUST Statements with DOES/DOES NOT status', size: 20 })], indent: { left: 400 } }),
        new Paragraph({ children: [new TextRun({ text: '• Evidence Requirements with collection status', size: 20 })], indent: { left: 400 } }),
        new Paragraph({ children: [new TextRun({ text: '• CSET Priority Rankings (1-106)', size: 20 })], indent: { left: 400 }, spacing: { after: 400 } }),
      );

      // ── Compliance Overview ──
      children.push(
        new Paragraph({ text: '1. Compliance Score Overview', heading: HeadingLevel.HEADING_1, spacing: { before: 400, after: 200 } }),
        new Paragraph({ children: [
          new TextRun({ text: 'Overall Implementation Rate: ', bold: true }),
          new TextRun({ text: `${summary.implementation_rate}%`, bold: true, color: summary.implementation_rate >= 80 ? '10B981' : summary.implementation_rate >= 50 ? 'F59E0B' : 'EF4444' }),
        ], spacing: { after: 100 } }),
        new Paragraph({ children: [
          new TextRun({ text: `Compliant: ${score.summary.compliant}  |  Non-Compliant: ${score.summary.nonCompliant}  |  N/A: ${score.summary.notApplicable}  |  Total: ${score.summary.total}` }),
        ], spacing: { after: 100 } }),
        new Paragraph({ children: [
          new TextRun({ text: `MUST Statements Met: ${summary.mustStatements.met}/${summary.mustStatements.total}  |  Evidence Collected: ${summary.evidence.collected}/${summary.evidence.total}` }),
        ], spacing: { after: 200 } }),
      );

      // Function scores table
      children.push(new Paragraph({ text: '2. CSF Function Scores', heading: HeadingLevel.HEADING_1, spacing: { before: 400, after: 200 } }));
      const funcTableRows = [
        new TableRow({
          tableHeader: true,
          children: ['Function', 'Compliant', 'Non-Compliant', 'N/A', 'Total', 'Score'].map(h =>
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: h, bold: true, color: 'FFFFFF', size: 18 })], alignment: AlignmentType.CENTER })],
              shading: { fill: '8B5CF6' },
              width: { size: h === 'Function' ? 30 : 14, type: WidthType.PERCENTAGE },
            })
          ),
        }),
        ...score.familyScores.map(f => new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: `${f.abbrev} - ${f.name}`, size: 18 })] })] }),
            ...[f.compliant, f.nonCompliant, f.notApplicable, f.totalControls, `${f.percentComplete}%`].map(v =>
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: String(v), size: 18 })], alignment: AlignmentType.CENTER })] })
            ),
          ],
        })),
      ];
      children.push(new Table({ rows: funcTableRows, width: { size: 100, type: WidthType.PERCENTAGE } }));

      // ── Detailed Controls ──
      children.push(new Paragraph({ text: '3. Detailed Security Controls', heading: HeadingLevel.HEADING_1, spacing: { before: 400, after: 200 } }));
      detailedData.categories.forEach(func => {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: `${func.abbrev} - ${func.name}`, bold: true, size: 24, color: '7C3AED' })],
            spacing: { before: 300, after: 100 },
          }),
          new Paragraph({
            children: [new TextRun({ text: `${func.stats.yes} Compliant  |  ${func.stats.no} Non-Compliant  |  ${func.stats.na} N/A  |  ${func.controls.length} Total`, size: 18, color: '6B7280' })],
            spacing: { after: 200 },
          }),
        );

        func.controls.forEach(ctrl => {
          const statusColor = ctrl.response.answer === 'Y' ? '10B981' : ctrl.response.answer === 'NA' ? '6B7280' : 'EF4444';
          const statusText = ctrl.response.answer === 'Y' ? 'IMPLEMENTED' : ctrl.response.answer === 'NA' ? 'N/A' : 'NOT IMPLEMENTED';
          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: `${ctrl.id}`, bold: true, size: 20 }),
                new TextRun({ text: ctrl.priorityNumber ? `  [P${ctrl.priorityNumber}]` : '', size: 18, color: '6B7280' }),
                new TextRun({ text: `  —  ${statusText}`, bold: true, size: 18, color: statusColor }),
              ],
              spacing: { before: 150, after: 50 },
              shading: { fill: 'F8FAFC' },
            }),
            new Paragraph({ children: [new TextRun({ text: ctrl.text, size: 18 })], spacing: { after: 50 }, indent: { left: 200 } }),
          );
          if (ctrl.mustStatement) {
            const mustColor = ctrl.mustStatus === 'DOES' ? '10B981' : ctrl.mustStatus === 'DOES_NOT' ? 'EF4444' : '6B7280';
            const mustLabel = ctrl.mustStatus === 'DOES' ? 'DOES: ' : ctrl.mustStatus === 'DOES_NOT' ? 'DOES NOT: ' : 'MUST: ';
            children.push(new Paragraph({
              children: [
                new TextRun({ text: mustLabel, bold: true, size: 18, color: mustColor }),
                new TextRun({ text: ctrl.mustStatement, size: 16, italics: true }),
              ], indent: { left: 400 }, spacing: { after: 50 },
            }));
          }
          if (ctrl.evidenceRequired.length > 0) {
            ctrl.evidenceRequired.forEach((ev, idx) => {
              const collected = ctrl.evidenceChecklist[String(idx)];
              children.push(new Paragraph({
                children: [new TextRun({ text: `${collected ? '✓' : '○'} ${ev}`, size: 16, color: collected ? '10B981' : '6B7280' })],
                indent: { left: 600 },
              }));
            });
          }
          if (ctrl.response.responseText) {
            children.push(new Paragraph({
              children: [new TextRun({ text: `Response: "${ctrl.response.responseText}"`, size: 16, italics: true, color: '6B7280' })],
              indent: { left: 400 }, spacing: { after: 100 },
            }));
          }
        });
      });

      // ── Prioritization ──
      children.push(new Paragraph({ text: '4. CSET Security Control Prioritization', heading: HeadingLevel.HEADING_1, spacing: { before: 400, after: 200 } }));
      const priTableRows = [
        new TableRow({
          tableHeader: true,
          children: ['P#', 'Control', 'Function', 'Status', 'MUST', 'Evidence'].map(h =>
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: h, bold: true, color: 'FFFFFF', size: 16 })], alignment: AlignmentType.CENTER })],
              shading: { fill: '8B5CF6' },
            })
          ),
        }),
        ...priorityData.data.map(r => {
          const statusColor = r.status === 'Y' ? '10B981' : r.status === 'NA' ? '6B7280' : 'EF4444';
          return new TableRow({
            children: [
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: `P${r.sort}`, size: 16, bold: true })], alignment: AlignmentType.CENTER })] }),
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: r.secCntrl, size: 16 })] })] }),
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: r.function, size: 16 })] })] }),
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: r.status === 'Y' ? 'Yes' : r.status === 'NA' ? 'N/A' : 'No', size: 16, color: statusColor, bold: true })], alignment: AlignmentType.CENTER })] }),
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: r.mustStatus || '-', size: 16 })], alignment: AlignmentType.CENTER })] }),
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: `${r.evidenceCollected}/${r.evidenceTotal}`, size: 16 })], alignment: AlignmentType.CENTER })] }),
            ],
          });
        }),
      ];
      children.push(new Table({ rows: priTableRows, width: { size: 100, type: WidthType.PERCENTAGE } }));

      const wordDoc = new Document({ sections: [{ children }] });
      const blob = await Packer.toBlob(wordDoc);
      saveAs(blob, `NIST_CSF_SSP_Report_${new Date().toISOString().slice(0,10)}.docx`);
    } catch (err) {
      console.error('Word generation error:', err);
    } finally {
      setGeneratingWord(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="nist-csf-ssp-report" className="min-h-screen bg-slate-950">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-950/80 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(`/assessment/${id}`)} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-bold text-white">System Security Plan</h1>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-violet-500/20 text-violet-400 border border-violet-500/30">NIST CSF 2.0</span>
                </div>
                <p className="text-sm text-slate-500">{summary?.assessmentName || 'NIST CSF 2.0 Assessment'} &bull; NIST SP 800-18 Rev 1</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-bold px-3 py-1.5 rounded-full ${
                (summary?.implementation_rate || 0) >= 80 ? 'bg-emerald-500/20 text-emerald-400' :
                (summary?.implementation_rate || 0) >= 50 ? 'bg-amber-500/20 text-amber-400' :
                'bg-red-500/20 text-red-400'
              }`}>
                {summary?.implementation_rate || 0}% Implemented
              </span>
              <button
                data-testid="export-pdf-btn"
                onClick={generatePDF}
                disabled={generating}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/10 transition-colors disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                {generating ? 'Generating...' : 'PDF'}
              </button>
              <button
                data-testid="export-word-btn"
                onClick={generateWordDoc}
                disabled={generatingWord}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-400 border border-blue-500/30 rounded-lg hover:bg-blue-500/10 transition-colors disabled:opacity-50"
              >
                <FileText className="w-4 h-4" />
                {generatingWord ? 'Generating...' : 'Word'}
              </button>
              <button
                onClick={() => navigate(`/assessment/${id}/readiness`)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-cyan-400 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/10 transition-colors"
              >
                <Shield className="w-4 h-4" />
                Readiness
              </button>
            </div>
          </div>
          {/* Tabs */}
          <div className="flex items-center gap-1 -mb-px">
            {TABS.map(tab => (
              <button
                key={tab.key}
                data-testid={`ssp-tab-${tab.key}`}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-violet-500 text-violet-400'
                    : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'dashboard' && <DashboardTab summary={summary} score={score} />}
        {activeTab === 'score' && <ComplianceScoreTab score={score} />}
        {activeTab === 'overview' && <SystemOverviewTab summary={summary} />}
        {activeTab === 'controls' && <DetailedControlsTab assessmentId={id} />}
        {activeTab === 'evidence' && <EvidenceTab assessmentId={id} />}
        {activeTab === 'prioritization' && <PrioritizationTab assessmentId={id} />}
      </div>
    </div>
  );
}
